var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { proxy, subscribe } from 'valtio';
import { fieldsToState, getFirstInvalidFieldName, stateToFields, update, validate, } from './state';
export { emailValidator, ukPhoneValidator, ukPostcodeValidator, createRegexValidator, VALIDATION_REGEXES, REQUIRED_MESSAGE, } from './validator';
export { wrapField } from './wrap';
/**
 * A React hook that can be used to manage a form's validation and submission
 * process.
 *
 * It needs to be supplied with an initial state and a schema. In the schema
 * you can describe how the form should be validated, as well as other field
 * relationships.
 *
 * You can pass the type of the state explicitly as a type parameter, but this
 * defaults to the the type of the initial state, so if you pass in a
 * well-typed state object you do not need to pass in the type explictily.
 *
 * The state type is used throughout to ensure both callbacks and validators
 * are well-typed. You define this state type yourself but often it is implicit
 * as TypeScript derives it.
 *
 * Example of implicit state type:
 *
 * ```typescript
 * const { fields, submit } = useForm({
 *    initialState: { // TS figures out this is { foo: string }
 *      foo: '',
 *    },
 *    schema: {
 *      foo: {
 *        required: true
 *      }
 *    },
 *    submitCallback: async (param) => {
 *      // some server interaction
 *    },
 *    compatibility: false,
 *  })
 * ```
 *
 * If you have a more complicated state type with optional values, it cannot
 * be inferred successfully by Typescript. In that case you should
 * be more explicit about the type of `initialState`:
 *
 * ```typescript
 * type State = {
 *    foo: number | undefined
 * }
 *
 * const initialState: State = { // we helpfully supply the State explicitly here
 *    foo: undefined // typescript cannot infer: number | undefined
 * }
 *
 * const { fields, submit } = useForm({
 *    initialState,
 *    schema: {
 *      foo: {
 *        required: true
 *      }
 *    },
 *    submitCallback: async (param) => {
 *      // some server interaction
 *    },
 *    compatibility: false,
 *  })
 * ```
 */
const useForm = ({ initialState, schema, submitCallback = noopCallbackAsync, firstErrorCallback = defaultFirstErrorCallback, errorCallback = noopCallback, resetCallback = noopCallback, compatibility = true, }) => {
    const submitCallbacks = useCallback((fields) => {
        const invalidFieldName = getFirstInvalidFieldName(fields);
        if (invalidFieldName != null) {
            firstErrorCallback(invalidFieldName, fields);
            errorCallback(fields);
            return;
        }
        // everything okay, no validation error
        void submitCallback(fieldsToState(fields));
    }, [errorCallback, submitCallback, firstErrorCallback]);
    // console.log('useForm hook');
    const fieldsProxy = useMemo(() => proxy(stateToFields(initialState, schema)), [initialState, schema]);
    const fieldsRef = useRef(fieldsProxy);
    // TODO: the support for updating fields causes more trouble than its worth
    // as it makes valtio use unreliable if we read a field from within the same
    // component where use `useForm`. So I've disabled this for now. This means that
    // NextjS fast refresh is currently flaky.
    // useEffect(() => {
    //   // in compatibility mode this causes infinite recursion, so
    //   // we turn this behavior off.
    //   if (compatibility) {
    //     return;
    //   }
    //   // when we change either initialState or schema, we update
    //   // the fields so we see the appropriate behavior change in
    //   // NextJS fast refresh
    //   updateFields(fieldsRef.current, initialState, schema);
    // }, [initialState, schema, compatibility]);
    const fields = fieldsRef.current;
    const change = useCallback((name, value) => {
        fields[name].setValue(value);
    }, [fields]);
    const submit = useCallback(() => {
        validate(fields);
        submitCallbacks(fields);
    }, [fields, submitCallbacks]);
    const updateState = useCallback((state) => {
        update(fields, state);
    }, [fields]);
    const reset = useCallback(() => {
        update(fields, initialState);
    }, [fields, initialState]);
    useEffect(() => {
        resetCallback(reset);
        // we disable the rule here, as there is a cycle between
        // useFormHook and useForm, which otherwise calls in an infinite
        // chain of calls. We only want this to be invoked once ever.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // in compatibility mode we want to rerender the component that uses
    // useForm each time an individual field changes. We accomplish this
    // by tracking its state.
    const [, setCounter] = useState(0);
    useEffect(() => {
        if (!compatibility) {
            return () => { };
        }
        return subscribe(fields, () => {
            // setting state will force an update
            setCounter((counter) => counter + 1);
        });
    });
    // I considered returning `fields` as a snapshot here. But that won't work
    // because it will not allow mutation. I've also tried returning an
    // additional `readFields` that is a snapshot besides fields, but while that
    // works, it causes too many rerenders (and after some more changes seems to
    // freeze out my browser). In the end it's best to use the valtio `useSnapshot` API
    // directly in React components that want to react to changes to the state.
    return {
        fields,
        updateState,
        change,
        submit,
        reset,
    };
};
const defaultFirstErrorCallback = (fieldName, fields) => {
    var _a;
    return (_a = fields[fieldName]) === null || _a === void 0 ? void 0 : _a.focus();
};
const noopCallback = () => {
    // do nothing
};
const noopCallbackAsync = () => __awaiter(void 0, void 0, void 0, function* () {
    // do nothing asynchronously
});
export { useForm };
export default useForm;
